import React from "react";
import { Badge as SharedBadge, type BadgeProps as SharedBadgeProps, cn } from "@ndd/ui";

// AlwaysAI's domain badge vocabulary layered on the @ndd/ui primitive via
// className, per ADR-0024: app-specific spectrums stay out of the shared
// package (see the "Composed: app rating spectrum" design-system story).
//
// Under the always theme a rating or run status is a highlighter behind the
// word instead of a filled pill (ADR-0004, 2026-10-22 amendment). Badge takes
// a className, so the theme reaches it through values only the theme defines
// (client/src/styles.css), each read with the classic value as its fallback,
// never through brand: utilities, which would beat a caller's className. With
// the theme off every badge renders exactly as before.

/** The highlighter's three marks: at or above the bar, not yet, below. */
export type HighlightTone = "yes" | "meh" | "no";

const HIGHLIGHT_MARK: Record<HighlightTone, string> = {
  yes: "bg-(image:--highlight-yes)",
  meh: "bg-(image:--highlight-meh)",
  no: "bg-(image:--highlight-no)",
};

const domainVariants = {
  // Rating severity — variants below form an ordered spectrum (best → worst).
  // Edit one and you may need to rebalance the others.
  exceeds:
    "border-transparent bg-(--highlight-fill,var(--color-forest-500)) text-(color:--highlight-ink,var(--color-forest-100)) bg-(image:--highlight-yes)",
  great:
    "border-transparent bg-(--highlight-fill,var(--color-success-soft)) text-(color:--highlight-ink,var(--color-success)) bg-(image:--highlight-yes)",
  meets:
    "border-transparent bg-(--highlight-fill,var(--color-sunlight-100)) text-(color:--highlight-ink,var(--color-sunlight-500)) bg-(image:--highlight-yes)",
  "needs-work":
    "border-transparent bg-(--highlight-fill,var(--color-orange-100)) text-(color:--highlight-ink,var(--color-orange-500)) bg-(image:--highlight-meh)",
  "needs-improvement":
    "border-transparent bg-(--highlight-fill,var(--color-danger-soft)) text-(color:--highlight-ink-no,var(--destructive)) bg-(image:--highlight-no)",
  unknown:
    "border-2 border-dashed border-destructive bg-danger-soft/40 text-destructive",
  // Run status ("error-status" keeps its legacy Badge name so call sites
  // ported unchanged; it means a failed run). Under the theme a queued or
  // analyzing run is a quiet track mark, not blue (D2).
  queued:
    "border-transparent bg-(--highlight-fill,var(--color-sunlight-100)) text-(color:--highlight-ink,var(--color-sunlight-500)) bg-(image:--highlight-meh)",
  analyzing:
    "border-transparent bg-(--highlight-fill,var(--color-sky-100)) text-(color:--highlight-ink,var(--color-sky-500)) bg-(image:--highlight-meh)",
  "error-status":
    "border-transparent bg-(--highlight-fill,var(--color-danger-soft)) text-(color:--highlight-ink-no,var(--destructive)) bg-(image:--highlight-no)",
} as const;

type DomainVariant = keyof typeof domainVariants;

const HIGHLIGHT_TONES: Partial<Record<DomainVariant, HighlightTone>> = {
  exceeds: "yes",
  great: "yes",
  meets: "yes",
  "needs-work": "meh",
  "needs-improvement": "no",
  queued: "meh",
  analyzing: "meh",
  "error-status": "no",
};

// The highlighter's shape for each shared Badge size, with that size's
// classic radius, padding and weight as the fallbacks.
const HIGHLIGHT_SHAPE: Record<NonNullable<SharedBadgeProps["size"]>, string> = {
  sm: "rounded-(--highlight-radius,calc(infinity*1px)) px-(--highlight-px,0.75rem) py-(--highlight-py,0.25rem) font-(family-name:--highlight-font) font-(weight:--highlight-weight,500)",
  md: "rounded-(--highlight-radius,calc(infinity*1px)) px-(--highlight-px,0.75rem) py-(--highlight-py,0.25rem) font-(family-name:--highlight-font) font-(weight:--highlight-weight,600)",
  lg: "rounded-(--highlight-radius,calc(infinity*1px)) px-(--highlight-px,1rem) py-(--highlight-py,0.5rem) font-(family-name:--highlight-font) font-(weight:--highlight-weight,500)",
};

export interface BadgeProps extends Omit<SharedBadgeProps, "variant"> {
  variant?: NonNullable<SharedBadgeProps["variant"]> | DomainVariant | "category";
}

function isDomainVariant(variant: BadgeProps["variant"]): variant is DomainVariant {
  return typeof variant === "string" && variant in domainVariants;
}

function highlightTone(variant: BadgeProps["variant"]): HighlightTone | null {
  if (!isDomainVariant(variant)) return null;

  return HIGHLIGHT_TONES[variant] ?? null;
}

/**
 * The mark a domain variant draws behind its word under the theme, or null.
 * A badge wider than its word (RatingBadge's fixed slot) draws the mark on an
 * inner span instead, so it stays behind the word.
 */
export function highlightMark(variant: BadgeProps["variant"]): string | null {
  const tone = highlightTone(variant);

  return tone ? HIGHLIGHT_MARK[tone] : null;
}

export function Badge({ variant, size, className, ...props }: BadgeProps) {
  // Neutral pill for categorical distinctions (session type, red flag
  // category, etc.). Per ADR 0004: category gets text/icon, not color — the
  // shared "neutral" variant is exactly that treatment.
  if (variant === "category") {
    return (
      <SharedBadge variant="neutral" size={size} className={className} {...props} />
    );
  }

  if (isDomainVariant(variant)) {
    const shape = highlightTone(variant) ? HIGHLIGHT_SHAPE[size ?? "sm"] : undefined;

    return (
      <SharedBadge
        variant="muted"
        size={size}
        className={cn(domainVariants[variant], shape, className)}
        {...props}
      />
    );
  }

  return <SharedBadge variant={variant} size={size} className={className} {...props} />;
}
